export const SUPPORT_LOG_SCHEMA_VERSION = 'seaus.support-log.v1';
export const SUPPORT_LOG_STORAGE_KEY = 'seaus.support-log.entries.v1';

const SUPPORT_LOG_RETENTION_MS = 48 * 60 * 60 * 1000;
const SUPPORT_LOG_MAX_ENTRIES = 300;
const SUPPORT_LOG_MAX_STORAGE_CHARS = 256_000;
const SUPPORT_LOG_MAX_STRING_LENGTH = 500;
const SUPPORT_LOG_MAX_ARRAY_LENGTH = 40;
const SUPPORT_LOG_MAX_OBJECT_KEYS = 40;
const SUPPORT_LOG_MAX_DEPTH = 4;
const SUPPORT_LOG_FLUSH_DELAY_MS = 1000;
const REDACTED_DETAIL_KEY = /(passphrase|secret|access.?token|auth|firmware.?payload|binary.?payload)/i;
const OMITTED_DETAIL_KEY = /^macAddress$/i;
const SENSITIVE_QUERY_PARAM =
  /([?&](?:access_token|id_token|token|api_key|key|sig|signature|x-amz-signature|code)=)[^&#\s]*/gi;
const AUTHORIZATION_VALUE = /\b(Bearer|Basic)\s+[a-zA-Z0-9._~+/-]{6,}=*/gi;

export type SupportLogLevel = 'debug' | 'info' | 'warning' | 'error';

export type SupportLogEntry = {
  timestampIso: string;
  sessionId: string;
  level: SupportLogLevel;
  category: string;
  event: string;
  details?: Record<string, unknown>;
};

export type SupportLogDocument = {
  schemaVersion: typeof SUPPORT_LOG_SCHEMA_VERSION;
  exportedAtIso: string;
  currentSessionId: string;
  environment: {
    userAgent: string;
    platform: string;
    language: string;
    page: string;
  };
  currentState: Record<string, unknown>;
  entries: SupportLogEntry[];
};

const currentSessionId = createSessionId();
let cachedEntries: SupportLogEntry[] | null = null;
let flushTimer: number | null = null;

export function appendSupportLog(
  category: string,
  event: string,
  details?: Record<string, unknown>,
  level: SupportLogLevel = 'info'
): void {
  const sanitizedDetails = sanitizeRecord(details);
  const entry: SupportLogEntry = {
    timestampIso: new Date().toISOString(),
    sessionId: currentSessionId,
    level,
    category: normalizeLabel(category, 'app'),
    event: normalizeLabel(event, 'event'),
    ...(sanitizedDetails && Object.keys(sanitizedDetails).length > 0
      ? { details: sanitizedDetails }
      : {}),
  };

  cachedEntries = pruneEntries([...getEntries(), entry], Date.now());
  if (shouldFlushImmediately(entry)) {
    flushSupportLog();
  } else {
    scheduleSupportLogFlush();
  }
}

export function buildSupportLogDocument(
  currentState: Record<string, unknown> = {}
): SupportLogDocument {
  return {
    schemaVersion: SUPPORT_LOG_SCHEMA_VERSION,
    exportedAtIso: new Date().toISOString(),
    currentSessionId,
    environment: getEnvironmentSnapshot(),
    currentState: sanitizeRecord(currentState) ?? {},
    entries: pruneEntries(getEntries(), Date.now()),
  };
}

export function buildSupportLogJson(
  currentState: Record<string, unknown> = {}
): string {
  flushSupportLog();
  return `${JSON.stringify(buildSupportLogDocument(currentState), null, 2)}\n`;
}

export function buildSupportLogFileName(serialNumber?: string | null): string {
  const date = new Date().toISOString().slice(0, 10);
  const normalizedSerial = serialNumber?.trim().replace(/[^a-zA-Z0-9_-]+/g, '-');
  return normalizedSerial
    ? `seaus-support-log-${normalizedSerial}-${date}.json`
    : `seaus-support-log-${date}.json`;
}

export function clearSupportLog(): void {
  cancelScheduledFlush();
  cachedEntries = [];
  try {
    window.localStorage.removeItem(SUPPORT_LOG_STORAGE_KEY);
  } catch {
    // The in-memory log remains available when storage is blocked.
  }
}

export function flushSupportLog(): void {
  cancelScheduledFlush();
  const entries = pruneEntries(getEntries(), Date.now());
  const { bounded, serialized } = serializeBoundedEntries(entries);
  cachedEntries = bounded;
  try {
    window.localStorage.setItem(SUPPORT_LOG_STORAGE_KEY, serialized);
  } catch {
    // Keep the bounded in-memory copy when storage is unavailable or full.
  }
}

function getEntries(): SupportLogEntry[] {
  if (cachedEntries !== null) {
    return cachedEntries;
  }

  try {
    const stored = window.localStorage.getItem(SUPPORT_LOG_STORAGE_KEY);
    if (stored === null) {
      cachedEntries = [];
      return cachedEntries;
    }
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      cachedEntries = [];
      return cachedEntries;
    }
    cachedEntries = pruneEntries(
      parsed.filter(isSupportLogEntry).map(sanitizePersistedEntry),
      Date.now()
    );
    return cachedEntries;
  } catch {
    cachedEntries = [];
    return cachedEntries;
  }
}

function serializeBoundedEntries(
  entries: SupportLogEntry[]
): { bounded: SupportLogEntry[]; serialized: string } {
  let bounded = entries.slice(-SUPPORT_LOG_MAX_ENTRIES);
  let serialized = JSON.stringify(bounded);
  while (serialized.length > SUPPORT_LOG_MAX_STORAGE_CHARS && bounded.length > 1) {
    bounded = bounded.slice(Math.max(1, Math.ceil(bounded.length * 0.1)));
    serialized = JSON.stringify(bounded);
  }
  return { bounded, serialized };
}

function scheduleSupportLogFlush(): void {
  if (flushTimer !== null) {
    return;
  }
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushSupportLog();
  }, SUPPORT_LOG_FLUSH_DELAY_MS);
}

function cancelScheduledFlush(): void {
  if (flushTimer === null) {
    return;
  }
  window.clearTimeout(flushTimer);
  flushTimer = null;
}

function shouldFlushImmediately(entry: SupportLogEntry): boolean {
  const eventName = `${entry.category} ${entry.event}`.toLowerCase();
  return (
    entry.level === 'error' ||
    eventName.includes('disconnect') ||
    entry.category.toLowerCase() === 'support'
  );
}

function pruneEntries(entries: SupportLogEntry[], nowMs: number): SupportLogEntry[] {
  const cutoff = nowMs - SUPPORT_LOG_RETENTION_MS;
  return entries
    .filter((entry) => {
      const timestamp = Date.parse(entry.timestampIso);
      return Number.isFinite(timestamp) && timestamp >= cutoff;
    })
    .slice(-SUPPORT_LOG_MAX_ENTRIES);
}

function sanitizeRecord(
  value: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!value) {
    return undefined;
  }
  const sanitized = sanitizeValue(value, 0, new WeakSet<object>());
  return isPlainRecord(sanitized) ? sanitized : undefined;
}

function sanitizeValue(
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
  key = ''
): unknown {
  if (OMITTED_DETAIL_KEY.test(key)) {
    return undefined;
  }
  if (REDACTED_DETAIL_KEY.test(key)) {
    return '[redacted]';
  }
  if (value === null || typeof value === 'boolean' || typeof value === 'number') {
    return typeof value === 'number' && !Number.isFinite(value) ? String(value) : value;
  }
  if (typeof value === 'string') {
    const redacted = value
      .replace(SENSITIVE_QUERY_PARAM, '$1[redacted]')
      .replace(AUTHORIZATION_VALUE, '$1 [redacted]');
    return redacted.length > SUPPORT_LOG_MAX_STRING_LENGTH
      ? `${redacted.slice(0, SUPPORT_LOG_MAX_STRING_LENGTH)}…`
      : redacted;
  }
  if (value === undefined) {
    return undefined;
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeValue(value.message, depth + 1, seen, 'message'),
    };
  }
  if (typeof value !== 'object') {
    return String(value);
  }
  if (depth >= SUPPORT_LOG_MAX_DEPTH) {
    return '[max-depth]';
  }
  if (seen.has(value)) {
    return '[circular]';
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .slice(0, SUPPORT_LOG_MAX_ARRAY_LENGTH)
      .map((item) => sanitizeValue(item, depth + 1, seen));
  }
  if (ArrayBuffer.isView(value)) {
    return {
      type: value.constructor.name,
      byteLength: value.byteLength,
    };
  }

  const result: Record<string, unknown> = {};
  Object.entries(value)
    .slice(0, SUPPORT_LOG_MAX_OBJECT_KEYS)
    .forEach(([entryKey, entryValue]) => {
      const sanitized = sanitizeValue(entryValue, depth + 1, seen, entryKey);
      if (sanitized !== undefined) {
        result[entryKey] = sanitized;
      }
    });
  return result;
}

function getEnvironmentSnapshot(): SupportLogDocument['environment'] {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform || 'unknown',
    language: navigator.language || 'unknown',
    page: `${window.location.origin}${window.location.pathname}`,
  };
}

function isSupportLogEntry(value: unknown): value is SupportLogEntry {
  if (!isPlainRecord(value)) {
    return false;
  }
  return (
    typeof value.timestampIso === 'string' &&
    typeof value.sessionId === 'string' &&
    typeof value.level === 'string' &&
    typeof value.category === 'string' &&
    typeof value.event === 'string'
  );
}

function sanitizePersistedEntry(entry: SupportLogEntry): SupportLogEntry {
  const details = sanitizeRecord(entry.details);
  return {
    timestampIso: entry.timestampIso,
    sessionId: entry.sessionId,
    level: entry.level,
    category: normalizeLabel(entry.category, 'app'),
    event: normalizeLabel(entry.event, 'event'),
    ...(details && Object.keys(details).length > 0 ? { details } : {}),
  };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeLabel(value: string, fallback: string): string {
  const normalized = String(value ?? '').trim().slice(0, 80);
  return normalized || fallback;
}

function createSessionId(): string {
  try {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to a non-cryptographic correlation id.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
